import React, { useState } from 'react';
import { MermaidDiagram } from '@lightenna/react-mermaid-diagram';
import { Button, Modal, Table, Box } from '@amzn/awsui-components-console';
import CodeView from '@amzn/awsui-code-view-components-console/code-view';
import { cleanMermaidContent } from '../../utils/cleanMermaidContent';

const MermaidBlock: React.FC<{ content: string }> = ({ content }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const cleanedContent = cleanMermaidContent(content);

  return (
    <>
      <div
        style={{
          position: 'relative',
          border: '1px solid #e1e4e8',
          borderRadius: '6px',
          backgroundColor: '#ffffff',
          padding: '12px',
          margin: '8px 0',
        }}
      >
        <div style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 1 }}>
          <Button
            iconName="expand"
            variant="icon"
            onClick={() => setIsFullscreen(true)}
            ariaLabel="View diagram fullscreen"
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', overflow: 'auto', maxHeight: '400px' }}>
          <MermaidDiagram theme="default" securityLevel="strict">
            {cleanedContent}
          </MermaidDiagram>
        </div>
      </div>

      <Modal
        visible={isFullscreen}
        onDismiss={() => setIsFullscreen(false)}
        closeAriaLabel="Close fullscreen view"
        size="max"
        header="Data Flow Diagram"
      >
        <div
          style={{
            width: '100%',
            height: '70vh',
            overflow: 'auto',
            backgroundColor: '#ffffff',
            padding: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MermaidDiagram theme="default" securityLevel="strict">
            {cleanedContent}
          </MermaidDiagram>
        </div>
      </Modal>
    </>
  );
};

const HighlightedCodeBlock: React.FC<{ code: string; language: string }> = ({ code, language }) => {
  return (
    <div style={{ margin: '8px 0' }}>
      <CodeView
        content={code}
        lineNumbers={code.split('\n').length > 5}
        wrapLines
      />
    </div>
  );
};

/**
 * MarkdownTable component that converts markdown table children to Cloudscape Table
 */
interface MarkdownTableProps {
  children: React.ReactNode;
}

const MarkdownTable: React.FC<MarkdownTableProps> = ({ children }) => {
  // Extract headers and rows from children
  const childArray = React.Children.toArray(children);
  let headers: string[] = [];
  let rows: Record<string, string>[] = [];

  childArray.forEach((child) => {
    if (!React.isValidElement(child)) return;
    
    if (child.type === 'thead' || (child.props as any)?.node?.tagName === 'thead') {
      // Extract headers from thead
      const theadChildren = React.Children.toArray((child.props as any).children);
      theadChildren.forEach((tr) => {
        if (React.isValidElement(tr)) {
          const thChildren = React.Children.toArray((tr.props as any).children);
          headers = thChildren.map((th) => {
            if (React.isValidElement(th)) {
              return extractTextContent((th.props as any).children);
            }
            return '';
          });
        }
      });
    } else if (child.type === 'tbody' || (child.props as any)?.node?.tagName === 'tbody') {
      // Extract rows from tbody
      const tbodyChildren = React.Children.toArray((child.props as any).children);
      tbodyChildren.forEach((tr) => {
        if (React.isValidElement(tr)) {
          const tdChildren = React.Children.toArray((tr.props as any).children);
          const rowData: Record<string, string> = {};
          tdChildren.forEach((td, idx) => {
            if (React.isValidElement(td)) {
              const key = headers[idx] || `col${idx}`;
              rowData[key] = extractTextContent((td.props as any).children);
            }
          });
          if (Object.keys(rowData).length > 0) {
            rows.push(rowData);
          }
        }
      });
    }
  });

  // If we couldn't parse properly, fall back to styled HTML table
  if (headers.length === 0) {
    return (
      <div style={{ overflowX: 'auto', margin: '12px 0' }}>
        <table className="awsui-table-styled">
          {children}
        </table>
      </div>
    );
  }

  // Create column definitions for Cloudscape Table
  const columnDefinitions = headers.map((header, idx) => ({
    id: `col-${idx}`,
    header: header,
    cell: (item: Record<string, string>) => item[header] || '',
    sortingField: header,
  }));

  return (
    <div style={{ margin: '12px 0' }}>
      <Table
        columnDefinitions={columnDefinitions}
        items={rows}
        variant="embedded"
        stripedRows
        wrapLines
        empty={
          <Box textAlign="center" color="inherit">
            No data
          </Box>
        }
      />
    </div>
  );
};

// Helper to extract text content from React children
function extractTextContent(children: React.ReactNode): string {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) {
    return children.map(extractTextContent).join('');
  }
  if (React.isValidElement(children)) {
    return extractTextContent((children.props as any).children);
  }
  return '';
}

/**
 * Shared ReactMarkdown component overrides used across all chat markdown rendering.
 * Detects fenced code blocks with language tags and renders:
 * - `mermaid` blocks as interactive diagrams via MermaidDiagram
 * - `sql`/`python`/`pyspark` blocks with syntax highlighting via CodeView
 * - Everything else as standard <pre><code>
 * - Tables rendered using Cloudscape Table component
 */
export const markdownComponents = {
  p: ({ children }: any) => <div className="chat-md-p">{children}</div>,
  strong: ({ children }: any) => <strong>{children}</strong>,
  em: ({ children }: any) => <em>{children}</em>,
  pre: ({ children }: any) => {
    const codeChild = React.Children.toArray(children).find(
      (child): child is React.ReactElement => React.isValidElement(child),
    ) as React.ReactElement<any> | undefined;

    if (codeChild) {
      const className: string = codeChild.props?.className || '';
      const langMatch = /language-(\w+)/.exec(className);
      const lang = langMatch?.[1];
      const codeText = String(codeChild.props?.children ?? '').replace(/\n$/, '');

      if (lang === 'mermaid') {
        return <MermaidBlock content={codeText} />;
      }

      if (lang && ['sql', 'python', 'pyspark'].includes(lang)) {
        return <HighlightedCodeBlock code={codeText} language={lang} />;
      }
    }

    return <pre className="chat-md-code-block">{children}</pre>;
  },
  code: ({ className, children, ...props }: any) => {
    const isInline = !className;
    if (isInline) {
      return <code className="chat-md-code-inline">{children}</code>;
    }
    return <code className={className} {...props}>{children}</code>;
  },
  ul: ({ children }: any) => <ul className="chat-md-ul">{children}</ul>,
  ol: ({ children }: any) => <ol className="chat-md-ol">{children}</ol>,
  li: ({ children }: any) => <li className="chat-md-li">{children}</li>,
  a: ({ href, children }: any) => (
    <a href={href} className="chat-md-link" target="_blank" rel="noopener noreferrer">{children}</a>
  ),
  h1: ({ children }: any) => <h1 className="chat-md-h1">{children}</h1>,
  h2: ({ children }: any) => <h2 className="chat-md-h2">{children}</h2>,
  h3: ({ children }: any) => <h3 className="chat-md-h3">{children}</h3>,
  // Table support using Cloudscape Table
  table: ({ children }: any) => <MarkdownTable>{children}</MarkdownTable>,
  thead: ({ children }: any) => <thead>{children}</thead>,
  tbody: ({ children }: any) => <tbody>{children}</tbody>,
  tr: ({ children }: any) => <tr>{children}</tr>,
  th: ({ children }: any) => <th>{children}</th>,
  td: ({ children }: any) => <td>{children}</td>,
};
